import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect, useLayoutEffect, useRef, useState, type CSSProperties, type ReactNode } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { NewEventWizard } from '../planning/NewEventWizard';
import { PACKLISTS_RESOURCE } from '../packlists/types';
import { settleCreated, type CreatedInPanel } from './afterCreate';
import { NewRentalForm } from './NewRentalForm';
import { useUrlState } from '../nav/urlState';
import { dropDraft } from '../nav/drafts';
import { api, ApiError } from '../api';
import { can, canPlan, fieldVisible } from '../auth/me';
import { useMe } from '../auth/useMe';
import { daysParam, describeEvent, EMPTY_SELECTION, eventDays, eventName, eventsListUrl, movedEvent, newOnDayUrl, nextSelection, pickedAfterOpen, filterCalendar, heading, rangeFor, selectionSummary, shiftToDay, truncatedNote, shiftAnchor, titleDates, todayInWarsaw, withClientColors, withDates, type CalendarEvent, type CalendarView, type DaySelection } from './calendar';
import { markStyle, useContractorColors } from '../contractors/colors';
import { MonthView, PhoneMonthView, PhoneWeekView, WeekView, YearView, type Interact } from './CalendarViews';
import { ignoresShortcuts, isMacLike, keyZoneOf, shortcutOf, type KeyZone } from './shortcuts';
import { applyUndo, CHANGED_SINCE, popUndo, pushUndo } from './undo';
import { refreshEvents } from './refresh';
import { CREW_KEY, CREW_RESOURCE, crewNames } from '../crew/types';
import '../crew/crew.css';
import { EventDetails, type EventTab } from './EventDetails';
import { EventForm } from './EventForm';
import { EventList } from './EventList';
import { loadSplit, PanelSplitter, saveSplit } from './PanelSplitter';
import { SidePanel } from './SidePanel';
import { leaveGuarded } from '../ui/unsaved';
import { addDays, EVENT_STATUSES, EVENTS_KEY, EVENTS_RESOURCE, formatDay, formatEventRange, formatSegments, type EventStatus, type EventView } from './labels';
import './calendar.css';
import { TaskLegendKey, taskDayExtra, useCalendarTasks, useTaskLayer } from '../company-tasks/CalendarTasks';
import { TaskDialog } from '../company-tasks/TaskDialog';
import { scopeOf as taskScope } from '../company-tasks/types';
import { CalendarFilter } from './CalendarFilter';
import { DialogLayer } from '../ui/Dialog';
import { useDraftingPick, type CalendarLink } from './calendarLink';
import { dragSelection, syncSelection } from './pickDates';
import { Notice } from '../ui/Notice';
import { PickedEventsBar } from './CombineOffer';
import { offerUrl } from '../quotes/offers/offers';
import type { OfferOf } from '../quotes/offers/types';
import { QUOTES_RESOURCE } from '../quotes/types';
import { RememberRuleHost } from './RememberRule';

type ListResponse = { events: EventView[]; truncated: boolean };

const VIEWS: { id: CalendarView; label: string }[] = [
  { id: 'week', label: 'Tydzień' },
  { id: 'month', label: 'Miesiąc' },
  { id: 'year', label: 'Rok' },
  { id: 'list', label: 'Lista' },
];
const VIEW_KEY = 'mek.events.view';

/** The chosen view is a per-browser convenience: storage may be missing or blocked, then the month is shown. */
function loadView(): CalendarView {
  try {
    const v = window.localStorage.getItem(VIEW_KEY);
    if (VIEWS.some((x) => x.id === v)) return v as CalendarView;
  } catch {
    /* storage unavailable */
  }
  return 'month';
}
function saveView(view: CalendarView): void {
  try {
    window.localStorage.setItem(VIEW_KEY, view);
  } catch {
    /* storage unavailable: the view just is not remembered */
  }
}

function useDebounced<T>(value: T, ms: number): T {
  const [debounced, setDebounced] = useState(value);
  useEffect(() => {
    const timer = setTimeout(() => setDebounced(value), ms);
    return () => clearTimeout(timer);
  }, [value, ms]);
  return debounced;
}

const PHONE = '(max-width: 899.98px)';
export function useIsPhone(): boolean {
  const [phone, setPhone] = useState(() => typeof window.matchMedia === 'function' && window.matchMedia(PHONE).matches);
  useEffect(() => {
    if (typeof window.matchMedia !== 'function') return;
    const mq = window.matchMedia(PHONE);
    const on = () => setPhone(mq.matches);
    mq.addEventListener('change', on);
    return () => mq.removeEventListener('change', on);
  }, []);
  return phone;
}

/** Names of clients by id (the events list carries only ids). A role without contractor access just gets no names. */
export function useClientNames(): Map<string, string> {
  const list = useQuery({ queryKey: ['contractors', 'names'], retry: false, queryFn: () => api<{ contractors: { id: string; name: string }[] }>('/api/contractors') });
  return new Map((list.data?.contractors ?? []).map((c) => [c.id, c.name]));
}

/**
 * What the side panel shows: an event's card (on `tab` right after it was made), or a new event / rental being made
 * (owner 2026-10-21 23:31: "Nowe wydarzenie, wypożyczenie itp. robimy w zakładce zamiast w nowym oknie").
 * `days`: several days picked with Shift / Cmd (owner 2026-10-21 00:44) — one new event of several terms.
 */
export type NewWhat = 'event' | 'rental';
/** `day`: the day clicked in the calendar to open the event — marked while its card is open (owner 2026-10-25). */
type Panel = { kind: 'event'; id: string; tab?: EventTab; day?: string } | { kind: 'new'; what: NewWhat; day?: string; days?: string[] } | null;
/** `nowe=` in the address: "1" a new event (as before), "wypozyczenie" a new rental. */
const NEW_PARAM: Record<NewWhat, string> = { event: '1', rental: 'wypozyczenie' };
const newWhatOf = (value: string | null): NewWhat | null => (value === NEW_PARAM.event ? 'event' : value === NEW_PARAM.rental ? 'rental' : null);

export function EventsPage() {
  const { me } = useMe();
  // P13: the view, the period, the search and the status filter live in the address ("Wstecz" comes back to them)
  const { params, update } = useUrlState();
  const search = params.get('szukaj') ?? '';
  const setSearch = (v: string) => update({ szukaj: v });
  const statusParam = params.get('status');
  const status: EventStatus | '' = statusParam && statusParam in EVENT_STATUSES ? (statusParam as EventStatus) : '';
  const setStatus = (v: EventStatus | '') => update({ status: v });
  // owner remark 15 and the note of 00:21: neither is remembered — after a reload the calendar shows all, without closed ones
  const [onlyConfirmed, setOnlyConfirmed] = useState(false);
  const [showClosed, setShowClosed] = useState(false);
  // F28 (owner remark 25): rentals to other companies on request only — off by default, not remembered
  const [showRentals, setShowRentals] = useState(false);
  /**
   * P6: the day picked by a click or the arrows (⌘V pastes there), the copied event (⌘C), the quick preview (space).
   * Owner 2026-10-21 00:44: Shift picks a run of days, Cmd / Ctrl one more day — "Nowe wydarzenie" makes one event of them.
   */
  const [selection, setSelection] = useState<DaySelection>(EMPTY_SELECTION);
  // „Oferta łączona” (owner 2026-10-30): events picked with ⌘-click for „Połącz w ofertę”
  // (kept with their client: picked events stay picked when the calendar moves to another month)
  const [pickedEvents, setPickedEvents] = useState<Array<{ id: string; name?: string | undefined; contractorId?: string | null | undefined }>>([]);
  const selectedDay = selection.anchor;
  const setSelectedDay = (day: string | null) => setSelection(day ? { days: [day], anchor: day, base: [] } : EMPTY_SELECTION);
  const picked = selection.days;
  /**
   * Owner 2026-10-22 14:40: while a new event / rental is open in the panel, the days picked in the calendar are its
   * dates — the last pick, handed to the form (a new object each time; null: nothing picked since it opened).
   */
  const [clip, setClip] = useState<string | null>(null);
  const [preview, setPreview] = useState<string | null>(null);
  const [deleting, setDeleting] = useState<{ id: string; name: string; version: number } | null>(null);
  // owner 2026-10-24 00:46: a drop that moves an event asks first; the bar waits on the new day until „Przenieś” is saved
  const [moving, setMoving] = useState<{ id: string; name: string; day: string; dates: string; version: number; before: { startsAt: string; endsAt: string }; saving: boolean } | null>(null);
  const [note, setNote] = useState<{ text: string; error?: boolean } | null>(null);
  const queryClient = useQueryClient();
  // without `widok` in the address: the view last chosen on this device
  const [storedView] = useState<CalendarView>(loadView);
  const viewParam = params.get('widok');
  const view: CalendarView = VIEWS.some((x) => x.id === viewParam) ? (viewParam as CalendarView) : storedView;
  const setView = (v: CalendarView) => update({ widok: v });
  const today = todayInWarsaw();
  const dayParam = params.get('data');
  const anchor = dayParam && /^\d{4}-\d{2}-\d{2}$/.test(dayParam) ? dayParam : today;
  const setAnchor = (day: string) => update({ data: day === today ? null : day });
  // `?wydarzenie=<id>` opens that event's card (the way back from the load / return screens), `&zakladka=` on a tab
  // (e.g. "kosztorys" from the "Kosztorysy" list) and `&pelny=1` with that tab over the whole window (owner remark 14).
  const [panel, setPanel] = useState<Panel>(() => {
    const id = params.get('wydarzenie');
    if (id) return { kind: 'event', id };
    // P13: "Nowe wydarzenie" / "Nowe wypożyczenie" are in the address too (`?nowe=1|wypozyczenie&dzien=` or `&dni=`),
    // so "Wstecz / Dalej" reopens the form; the old `/nowa-impreza`, `/nowe-wypozyczenie` lead here
    const day = params.get('dzien');
    const days = daysParam(params.get('dni'));
    const what = newWhatOf(params.get('nowe'));
    return what ? { kind: 'new', what, ...(days.length > 1 ? { days } : day ? { day } : {}) } : null;
  });
  // the link of a notification (bell) may lead here while the page is already open: open that card too
  const linked = params.get('wydarzenie');
  const urlTab = tabFromParam(params.get('zakladka'));
  // every click of such a link is a new history entry (key): it reopens the card also after it was closed
  const linkKey = useLocation().key;
  useEffect(() => {
    if (!linked) return;
    if (pending.current && pending.current.eventId !== linked) leaveCreated();
    setPanel((p) => (p?.kind === 'event' && p.id === linked ? p : { kind: 'event', id: linked }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [linked, linkKey]);
  const wide = panel !== null && params.get('pelny') === '1';
  /** The card's place in the address: which event, which tab, full view or not (replaced, so "back" leaves the screen). */
  const setCardUrl = (next: { id: string; tab?: EventTab; full?: boolean } | null, push = false) => {
    // the other parameters of the address (view, period, search, status) stay; the edit form (`edycja`) only on the same card
    const same = next !== null && next.id === params.get('wydarzenie');
    update(
      { wydarzenie: next?.id, zakladka: next?.tab && next.tab !== 'details' ? TAB_PARAMS[next.tab] : null, pelny: next?.full ? '1' : null, nowe: null, dzien: null, dni: null, ...(same ? {} : { edycja: null }) },
      { push },
    );
  };
  // the "Nowe wydarzenie" wizard lands here with a line about the template's gear (shown once, on that event)
  const location = useLocation();
  const [landing] = useState(() => {
    const notice = (location.state as { notice?: unknown } | null)?.notice;
    const id = params.get('wydarzenie');
    return typeof notice === 'string' && id ? { id, notice } : null;
  });
  // ... and only once: taken out of the history entry, so a reload or "back" does not say it again
  const navigate = useNavigate();
  useEffect(() => {
    if (location.state !== null && location.state !== undefined) navigate({ pathname: location.pathname, search: location.search }, { replace: true, state: null });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  const opener = useRef<HTMLElement | null>(null);
  /** A new event / rental just made in the panel: what waits for leaving its card (series, reminder), and its line. */
  const pending = useRef<CreatedInPanel | null>(null);
  const [createdNotice, setCreatedNotice] = useState<{ id: string; notice: string } | null>(null);
  /** EventForm (plain "Nowe wydarzenie") calls `onSaved` and then `onDone`: after a new event its card stays open. */
  const madeByForm = useRef(false);
  const meRef = useRef(me);
  meRef.current = me;
  // leaving the screen with the new card open: what waits for it is done all the same
  useEffect(
    () => () => {
      const p = pending.current;
      const who = meRef.current;
      pending.current = null;
      if (p && who) void settleCreated(p, who).then(() => refreshEvents(queryClient));
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [],
  );
  /** The calendar's share beside the card while it is dragged (null: the person's stored split). */
  const [split, setSplit] = useState<number | null>(null);
  const q = useDebounced(search.trim(), 250);
  const names = useClientNames();
  const clientColors = useContractorColors();
  const phone = useIsPhone();
  /**
   * Owner 2026-10-22 14:40: "Nowe wydarzenie" / "Nowe wypożyczenie" open beside the calendar (not on a phone, where
   * the panel covers it) — a click, a drag, Shift or Cmd / Ctrl in the calendar change its dates instead of starting
   * another new event, and the calendar marks the form's dates (the form says them: `calendarLink.onDays`).
   * `datesPick`: the last pick, handed to the form (a new object each time); dropped when picking ends (review M6).
   */
  const drafting = panel?.kind === 'new' && !phone;
  const [datesPick, setDatesPick] = useDraftingPick(drafting);
  // Searching finds events in every period, so the results are shown as a list whatever the chosen view.
  const searching = q !== '';
  const shown: CalendarView = searching ? 'list' : view;
  const range = rangeFor(shown, anchor);

  const list = useQuery({
    queryKey: [...EVENTS_KEY, 'list', q, status, range?.from ?? '', range?.to ?? ''],
    placeholderData: keepPreviousData,
    queryFn: () => api<ListResponse>(eventsListUrl({ q, status, range })),
  });
  // F18: the crew of the shown period (initials on bars, names on list rows), for roles that may see crews
  const crewRange = shown === 'list' || shown === 'year' ? null : range;
  const crew = useQuery({
    queryKey: [...CREW_KEY, 'calendar', crewRange?.from ?? '', crewRange?.to ?? ''],
    enabled: !!me && can(me, CREW_RESOURCE, 'view') && crewRange !== null,
    placeholderData: keepPreviousData,
    retry: false,
    queryFn: () => api<{ events: { id: string; unstaffed?: boolean; crew?: { personId?: string; personName?: string; status?: string }[] }[] }>(`/api/crew/calendar?from=${crewRange!.from}&to=${crewRange!.to}`),
  });
  // P7: the company tasks as a layer of the calendar (marks on their due days; a click opens the task)
  const taskLayer = useTaskLayer(me);
  const calendarTasks = useCalendarTasks(crewRange, taskLayer.on);
  const [taskId, setTaskId] = useState<string | null>(null);
  const keyHandler = useRef<(e: KeyboardEvent) => void>(() => undefined);
  // owner 2026-10-23 18:34: the calendar's keys only after a press or focus in the calendar, never in the card (shortcuts.ts)
  const keyZone = useRef<KeyZone>('calendar');
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => keyHandler.current(e);
    const onPress = (e: Event) => {
      keyZone.current = keyZoneOf(e.target, keyZone.current, 'press');
    };
    const onFocus = (e: Event) => {
      keyZone.current = keyZoneOf(e.target, keyZone.current, 'focus');
    };
    document.addEventListener('keydown', onKey);
    document.addEventListener('pointerdown', onPress, true);
    document.addEventListener('focusin', onFocus, true);
    return () => {
      document.removeEventListener('keydown', onKey);
      document.removeEventListener('pointerdown', onPress, true);
      document.removeEventListener('focusin', onFocus, true);
    };
  }, []);
  if (!me) return null;
  const dayExtra = taskLayer.on ? taskDayExtra(calendarTasks, today, setTaskId) : undefined;
  // Those who declined are left out; a role that may not see the status gets everyone (absent status = unknown, not declined).
  const crewOf = new Map((crew.data?.events ?? []).map((e) => [e.id, crewNames(e.crew ?? [])]));
  const unstaffed = new Set((crew.data?.events ?? []).filter((e) => e.unstaffed).map((e) => e.id));
  const listed: (CalendarEvent & { version?: number })[] = (list.data?.events ?? []).map((e) => {
    const names = crewOf.get(e.id);
    const shown = names ? { ...e, crew: names, ...(unstaffed.has(e.id) ? { unstaffed: true } : {}) } : e;
    return moving?.id === e.id && shown.startsAt && shown.endsAt ? movedEvent({ ...shown, startsAt: shown.startsAt, endsAt: shown.endsAt }, moving.day) : shown;
  });
  // owner 2026-10-23 20:49: the client's colour, only for an event whose client the person sees (rule 7a)
  // „Połącz w ofertę”: one offer of the picked events, then its page
  const combine = useMutation({
    mutationFn: (v: { clientId: string; eventIds: string[] }) => api<OfferOf>('/api/quotes/offers', { method: 'POST', body: v }),
    onSuccess: (of) => {
      setPickedEvents([]);
      navigate(offerUrl(of.offer.id));
    },
  });
  const events = withClientColors(filterCalendar(listed, { onlyConfirmed, showClosed, status, showRentals, searching }), clientColors);
  const selectedId = panel?.kind === 'event' ? panel.id : null;
  // owner 2026-10-23 13:34: another event or "Nowe…" while the open form has unsaved changes asks first
  const openPanel = (next: NonNullable<Panel>) => {
    const active = document.activeElement;
    leaveGuarded(() => openPanelNow(next, active), panelEl());
  };
  const openPanelNow = (next: NonNullable<Panel>, active: Element | null) => {
    // remember what opened the panel (a bar, a row, "Nowe wydarzenie"), so closing gives focus back to it
    if (active instanceof HTMLElement && !active.closest('.cal-panel')) opener.current = active;
    if (pending.current && !(next.kind === 'event' && next.id === pending.current.eventId)) leaveCreated();
    // owner 2026-10-22 (#89): picking another event while a card is open keeps its tab (e.g. "Obsada": who is where)
    const keep = panel?.kind === 'event' && next.kind === 'event';
    const keptTab = tabFromParam(params.get('zakladka'));
    setDatesPick(null);
    // owner 2026-10-25 13:12: one mark — the event's clicked day replaces the days picked before
    if (next.kind === 'event' && next.day) setSelection((cur) => pickedAfterOpen(cur, next.day!));
    setPanel(keep && next.kind === 'event' ? { ...next, tab: next.tab ?? keptTab } : next);
    if (next.kind === 'event') setCardUrl(keep ? { id: next.id, tab: next.tab ?? keptTab, full: wide } : { id: next.id, ...(next.tab ? { tab: next.tab } : {}) });
    else update({ wydarzenie: null, zakladka: null, pelny: null, edycja: null, nowe: NEW_PARAM[next.what], dzien: next.day ?? null, dni: next.days ? next.days.join(',') : null });
  };
  /** The new event / rental was made: the panel turns into its card on the tab asked for (owner 2026-10-21 23:31). */
  const onCreated = (made: CreatedInPanel) => {
    if (pending.current && pending.current.eventId !== made.eventId) leaveCreated();
    pending.current = made;
    setCreatedNotice({ id: made.eventId, notice: made.notice });
    // the picked days were the new event's dates: it is on them now, the calendar picks days as usual again
    setDatesPick(null);
    setSelection(EMPTY_SELECTION);
    setPanel({ kind: 'event', id: made.eventId, tab: made.tab });
    setCardUrl({ id: made.eventId, tab: made.tab, full: wide });
  };
  /** "Zamknij" (and Escape in the new event / rental form — never in the event card, owner 2026-10-23 21:58): with unsaved changes in the card's form, „Masz niezapisane zmiany” first (owner 2026-10-23 13:34). */
  /** The panel: its forms only (review I1 — a dialog over the page has its own question). */
  const panelEl = () => document.getElementById('event-panel');
  const closePanel = () => leaveGuarded(closePanelNow, panelEl());
  const closePanelNow = () => {
    // closing the card or the new-event form is giving up its unsaved values (P13)
    if (panel?.kind === 'new') dropDraft(`events.event:new:${panel.days?.join(',') ?? panel.day ?? ''}`);
    else if (panel?.kind === 'event' && params.get('edycja') === '1') dropDraft(`events.event:${panel.id}`);
    leaveCreated();
    setPanel(null);
    if (params.has('wydarzenie') || params.has('nowe')) setCardUrl(null);
    const back = opener.current;
    opener.current = null;
    requestAnimationFrame(() => {
      if (back?.isConnected) back.focus();
    });
  };
  /** "Pełny widok" / "Zmniejsz" of the new event / rental form. */
  const setNewFull = (full: boolean) => update({ pelny: full ? '1' : null }, { push: true });
  const open = (id: string, day?: string) => openPanel({ kind: 'event', id, ...(day ? { day } : {}) });
  /** Leaving a just-made card: its series and the "Uzupełnij sprzętówkę" reminder (if the packlist is empty). */
  function leaveCreated() {
    const p = pending.current;
    pending.current = null;
    if (!p || !me) return;
    setCreatedNotice(null);
    void settleCreated(p, me).then(async (text) => {
      if (text) setNote({ text });
      await refreshEvents(queryClient);
    });
  }
  const newOf = (what: NewWhat, days: string[], day: string | null): NonNullable<Panel> => (days.length > 1 ? { kind: 'new', what, days } : day ? { kind: 'new', what, day } : { kind: 'new', what });

  // ---------- P6: new event on a day, moving / copying / deleting from the calendar, undo, shortcuts ----------
  const mac = isMacLike();
  const cmd = (key: string) => (mac ? `⌘${key}` : `Ctrl+${key}`);
  const canMove = can(me, EVENTS_RESOURCE, 'edit') && fieldVisible(me, EVENTS_RESOURCE, 'startsAt') && fieldVisible(me, EVENTS_RESOURCE, 'endsAt');
  const canCopy = can(me, EVENTS_RESOURCE, 'create') && fieldVisible(me, EVENTS_RESOURCE, 'startsAt');
  const canDelete = can(me, EVENTS_RESOURCE, 'delete');
  const newOnDay = (day: string) => openPanel({ kind: 'new', what: 'event', day });
  /** The event as the list has it, or from the server (a card opened by a link may be outside the period shown). */
  const findEvent = async (id: string): Promise<CalendarEvent & { version: number }> => {
    const known = listed.find((e) => e.id === id);
    return known && known.version !== undefined ? { ...known, version: known.version } : api<EventView>(`/api/events/${id}`);
  };
  const act = async (work: () => Promise<string | null>) => {
    try {
      const text = await work();
      if (text) setNote({ text });
    } catch (error) {
      setNote({ text: error instanceof ApiError || (error instanceof Error && error.message.startsWith(CHANGED_SINCE)) ? error.message : 'Nie udało się. Spróbuj jeszcze raz.', error: true });
    }
    await refreshEvents(queryClient);
  };
  /** A drop that moves (not a copy): „Przenieść …?” first (owner 2026-10-24 00:46); nothing is saved until „Przenieś”. */
  const moveTo = (id: string, day: string) =>
    act(async () => {
      const e = await findEvent(id);
      if (!e.startsAt || !e.endsAt || eventDays({ startsAt: e.startsAt, endsAt: e.endsAt }).first === day) return null;
      const dated = { ...e, startsAt: e.startsAt, endsAt: e.endsAt };
      setMoving({ id, name: eventName(e), day, dates: titleDates(movedEvent(dated, day)), version: e.version, before: { startsAt: e.startsAt, endsAt: e.endsAt }, saving: false });
      return null;
    });
  /** „Przenieś”: saved; the bar stays on the new day until the calendar is fresh (a failure puts it back). */
  const confirmMove = () => {
    const m = moving;
    if (!m || m.saving) return;
    setMoving({ ...m, saving: true });
    void act(async () => {
      const saved = await api<EventView>(`/api/events/${m.id}`, { method: 'PATCH', body: { ...shiftToDay(m.before, m.day), version: m.version } });
      pushUndo({ kind: 'move', id: m.id, name: m.name, version: saved.version, before: m.before });
      return `Przeniesiono „${m.name}” na ${formatDay(m.day)}. ${cmd('Z')} cofa.`;
    }).finally(() => setMoving(null));
  };
  /** „Anuluj” or Esc: the bar goes back where it was. */
  const cancelMove = () => setMoving((m) => (m?.saving ? m : null));
  const copyTo = (id: string, day: string | null) =>
    act(async () => {
      const e = await findEvent(id);
      const first = day ?? (e.startsAt && e.endsAt ? eventDays({ startsAt: e.startsAt, endsAt: e.endsAt }).first : null);
      if (!first) return null;
      const copy = await api<EventView>(`/api/events/${id}/copy`, { method: 'POST', body: { firstDay: first } });
      pushUndo({ kind: 'copy', id: copy.id, name: eventName(e), version: copy.version });
      return `Skopiowano „${eventName(e)}” na ${formatDay(first)} (z danymi, miejscem i sprzętówką). ${cmd('Z')} cofa.`;
    });
  const askDelete = (id: string) =>
    act(async () => {
      const e = await findEvent(id);
      setDeleting({ id, name: eventName(e), version: e.version });
      return null;
    });
  const confirmDelete = () => {
    const d = deleting;
    if (!d) return;
    setDeleting(null);
    void act(async () => {
      await api(`/api/events/${d.id}?version=${d.version}`, { method: 'DELETE' });
      pushUndo({ kind: 'delete', id: d.id, name: d.name });
      if (panel?.kind === 'event' && panel.id === d.id) closePanelNow();
      return `Usunięto „${d.name}”. ${cmd('Z')} cofa.`;
    });
  };
  const undo = () =>
    act(async () => {
      const step = popUndo();
      return step ? applyUndo(step) : 'Nie ma nic do cofnięcia.';
    });
  const pickDates = (next: DaySelection) => {
    setSelection(next);
    setDatesPick({ days: next.days });
  };
  const calendarLink: CalendarLink | undefined = drafting ? { pick: datesPick, onDays: (days) => setSelection((cur) => syncSelection(cur, days)) } : undefined;
  const interact: Interact = {
    selectedDay,
    // owner 2026-10-25: the day an event was opened on is marked too (one way: the event's dates never follow the calendar)
    selectedDays: panel?.kind === 'event' && panel.day ? [...picked, panel.day] : picked,
    onSelectDay: drafting
      ? (day, mods) => pickDates(nextSelection(selection, day, mods))
      : (day, mods) => {
          // owner 2026-10-25 13:12: one mark — a picked day replaces the open event's clicked day
          setPanel((p) => (p?.kind === 'event' && p.day ? { kind: 'event', id: p.id, ...(p.tab ? { tab: p.tab } : {}) } : p));
          setSelection((cur) => nextSelection(cur, day, mods));
          // review M6: picking days with Shift / Cmd drops the events picked for an offer (one bar at a time)
          if (mods.range || mods.toggle) setPickedEvents([]);
        },
    // a double click is two clicks on that day (it would replace the form being filled)
    onNewOnDay: !drafting && (canPlan(me) || can(me, EVENTS_RESOURCE, 'create')) ? newOnDay : undefined,
    onPickRange: drafting ? (from, to, add) => pickDates(dragSelection(selection, from, to, add)) : undefined,
    // review I2: a click on an event picks its day — a half-filled form is never swapped for that card
    pickOnly: drafting,
    // review M6: one picking bar at a time — picking events drops the days picked with Shift / Cmd
    onToggleEvent: !drafting && can(me, QUOTES_RESOURCE, 'view') ? (id) => (setSelection((cur) => (cur.days.length > 1 ? EMPTY_SELECTION : cur)), setPickedEvents((p) => (p.some((x) => x.id === id) ? p.filter((x) => x.id !== id) : [...p, listed.find((x) => x.id === id) ?? { id }]))) : undefined,
    pickedEvents: pickedEvents.map((x) => x.id),
    drag: !phone && (canMove || canCopy) ? { onDrop: (id, day, copy) => void (copy ? canCopy && copyTo(id, day) : canMove && moveTo(id, day)) } : undefined,
  };
  keyHandler.current = (e: KeyboardEvent) => {
    if (e.key === 'Escape' && preview) {
      e.preventDefault();
      setPreview(null);
      return;
    }
    if (e.key === 'Escape' && pickedEvents.length > 0 && !e.defaultPrevented && !ignoresShortcuts(e.target, keyZone.current)) {
      e.preventDefault();
      setPickedEvents([]);
      return;
    }
    // owner 2026-10-21 00:44: Escape drops the days picked with Shift / Cmd (the card itself never closes on Escape)
    if (e.key === 'Escape' && picked.length > 1 && !e.defaultPrevented && !ignoresShortcuts(e.target, keyZone.current)) {
      e.preventDefault();
      // review I1: with a new event / rental being filled only the calendar's marks go (one way: its dates stay);
      // the next Escape follows the panel's own rule
      setSelection(EMPTY_SELECTION);
      return;
    }
    // an open task dialog (P7) owns the keyboard, like the delete question
    if (e.defaultPrevented || wide || deleting || moving || taskId || ignoresShortcuts(e.target, keyZone.current)) return;
    const shortcut = shortcutOf(e, mac);
    if (!shortcut) return;
    const chosen = selectedId;
    switch (shortcut.kind) {
      case 'undo':
        e.preventDefault();
        void undo();
        return;
      case 'copy': {
        const selection = window.getSelection();
        if (!chosen || (selection && !selection.isCollapsed)) return; // selected text: the browser copies it
        e.preventDefault();
        setClip(chosen);
        setNote({ text: `Skopiowano wydarzenie. Kliknij dzień i wciśnij ${cmd('V')}, żeby wkleić kopię.` });
        return;
      }
      case 'paste':
        if (!clip || !canCopy) return;
        e.preventDefault();
        if (selectedDay) void copyTo(clip, selectedDay);
        else setNote({ text: 'Kliknij dzień, na który wkleić kopię.' });
        return;
      case 'duplicate':
        if (!chosen || !canCopy) return;
        e.preventDefault(); // ⌘D would bookmark the page
        void copyTo(chosen, null);
        return;
      case 'delete':
        if (!chosen || !canDelete) return;
        e.preventDefault();
        void askDelete(chosen);
        return;
      case 'preview': {
        // only where space means nothing else: the page, the card's heading or the chosen event's own bar
        const t = e.target;
        const onBar = t instanceof Element && t.closest(`[data-event-id]`)?.getAttribute('data-event-id') === chosen;
        const onHeading = t instanceof HTMLElement && t.tagName === 'H2' && t.closest('.cal-panel') !== null;
        if (!chosen || !(t === document.body || onBar || onHeading)) return;
        e.preventDefault();
        setPreview(preview === chosen ? null : chosen);
        return;
      }
      case 'move-day': {
        if (shown === 'list') return;
        e.preventDefault();
        const ev = chosen ? withDates(listed).find((x) => x.id === chosen) : undefined;
        const base = selectedDay ?? (ev ? eventDays(ev).first : range && today >= range.from && today <= range.to ? today : (range?.from ?? today));
        const next = addDays(base, shortcut.days);
        setSelectedDay(next);
        if (range && (next < range.from || next > range.to)) setAnchor(next);
        return;
      }
    }
  };
  const previewed = preview ? listed.find((e) => e.id === preview) : undefined;
  const pickView = (v: CalendarView) => {
    // while searching the results are a list; picking a calendar view ends the search
    if (searching && v !== 'list') setSearch('');
    setView(v);
    saveView(v);
  };
  const calShare = split ?? loadSplit(me.id);
  const resizable = panel !== null && !phone && !wide;
  const renderList = (items: CalendarEvent[], empty: string) => <EventList events={items} names={names} selectedId={selectedId} onOpen={open} empty={empty} compact />;

  return (
    <section className="page wide events-page" data-feature-id="events.list">
      {/* owner 21.10 00:44: each takes the day picked in the calendar (`dzien=`), or the days picked with Shift / Cmd (`dni=`) */}
      {/* owner 20.10 23:37: the three ways to add on top — "Nowe wydarzenie" (main), "Nowe wypożyczenie", "Nowe zadanie
          firmowe" — each only for a role that may use it; the filters went into one field next to the search */}
      <div className="page-head">
        <h1>Wydarzenia</h1>
        <div className="events-tools">
          {/* one main button, in the side panel (owner 2026-10-21 23:31): the wizard for whoever may plan, the plain
              form for whoever may only add events */}
          {(canPlan(me) || can(me, EVENTS_RESOURCE, 'create')) && !(panel?.kind === 'new' && panel.what === 'event') && (
            <button className="btn primary" type="button" data-feature-id={canPlan(me) ? 'planning.wizard' : 'events.edit'} onClick={() => openPanel(newOf('event', picked, selectedDay))}>
              Nowe wydarzenie
            </button>
          )}
          {/* F28: a rental of gear to another company: client, days out and back, then its packlist */}
          {can(me, EVENTS_RESOURCE, 'create') && fieldVisible(me, EVENTS_RESOURCE, 'kind') && !(panel?.kind === 'new' && panel.what === 'rental') && (
            <button className="btn" type="button" data-feature-id="events.rentals" onClick={() => openPanel(newOf('rental', picked, selectedDay))}>
              Nowe wypożyczenie
            </button>
          )}
          {/* P7: a company task — the quick form of "Zadania", ready to type */}
          {taskScope(me, 'create') && (
            <Link className="btn" to={newOnDayUrl('/zadania?nowe=1', picked[0] ?? selectedDay)} data-feature-id="company-tasks.list">
              Nowe zadanie firmowe
            </Link>
          )}
        </div>
      </div>

      <div className="cal-toolbar">
        {shown !== 'list' ? (
          <div className="cal-nav">
            <button className="btn icon" type="button" aria-label="Poprzedni okres" onClick={() => setAnchor(shiftAnchor(view, anchor, -1))}>
              <Chevron dir="left" />
            </button>
            <button className="btn icon" type="button" aria-label="Następny okres" onClick={() => setAnchor(shiftAnchor(view, anchor, 1))}>
              <Chevron dir="right" />
            </button>
            <h2 aria-live="polite">{heading(view, anchor)}</h2>
            <button className="btn small" type="button" onClick={() => setAnchor(today)}>
              Dziś
            </button>
          </div>
        ) : (
          <p className="cal-list-note">{searching ? 'Wyniki wyszukiwania ze wszystkich terminów, od najpóźniejszego' : 'Wszystkie terminy, od najpóźniejszego'}</p>
        )}
        <div className="cal-filters" role="group" aria-label="Filtry kalendarza">
          <input type="search" aria-label="Szukaj" value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Szukaj: nazwa lub miejsce" autoComplete="off" />
          <CalendarFilter
            value={{ status, onlyConfirmed, showClosed, showRentals, showTasks: taskLayer.on }}
            options={{ rentals: fieldVisible(me, EVENTS_RESOURCE, 'kind'), tasks: taskLayer.available }}
            onChange={(v) => {
              if (v.status !== status) setStatus(v.status);
              setOnlyConfirmed(v.onlyConfirmed);
              setShowClosed(v.showClosed);
              setShowRentals(v.showRentals);
              if (v.showTasks !== taskLayer.on) taskLayer.toggle();
            }}
          />
        </div>
        <div className="segment" role="group" aria-label="Widok kalendarza">
          {VIEWS.map((v) => (
            <button key={v.id} type="button" aria-pressed={shown === v.id} onClick={() => pickView(v.id)}>
              {v.label}
            </button>
          ))}
        </div>
      </div>

      {drafting && shown !== 'list' && (
        <p className="cal-pick drafting" role="status" data-feature-id="events.segments">
          <strong>{picked.length > 0 ? selectionSummary(picked) : 'Nie wybrano dni'}</strong>
          <span>
            — daty {panel.what === 'rental' ? 'nowego wypożyczenia' : 'nowego wydarzenia'}. Kliknij dzień albo przeciągnij po dniach; Shift — ciąg dni, {mac ? '⌘' : 'Ctrl'} — osobne dni.
          </span>
        </p>
      )}
      {!drafting && pickedEvents.length > 0 && shown !== 'list' && (
        <PickedEventsBar
          events={pickedEvents}
          clientNames={names}
          clientHidden={!fieldVisible(me, EVENTS_RESOURCE, 'contractorId')}
          mayCombine={can(me, QUOTES_RESOURCE, 'create')}
          busy={combine.isPending}
          error={combine.error instanceof ApiError ? combine.error.message : combine.error ? 'Nie udało się utworzyć oferty' : null}
          onCombine={(clientId, eventIds) => combine.mutate({ clientId, eventIds })}
          onClear={() => setPickedEvents([])}
        />
      )}
      {!drafting && picked.length > 1 && pickedEvents.length === 0 && shown !== 'list' && (
        <p className="cal-pick" role="status" data-feature-id="events.segments">
          <strong>{selectionSummary(picked)}</strong>
          <span>— „Nowe wydarzenie” i „Nowe wypożyczenie” utworzą z nich jedno wydarzenie.</span>
          <button type="button" className="btn ghost small" onClick={() => setSelection(EMPTY_SELECTION)}>
            Wyczyść (Esc)
          </button>
        </p>
      )}
      {list.error && <p className="alert" role="alert">{list.error instanceof ApiError ? list.error.message : 'Nie udało się wczytać wydarzeń'}</p>}
      {list.data?.truncated && (
        <p className="notice">{truncatedNote(shown, searching)}</p>
      )}
      {note?.error && (
        <p className="alert cal-note" role="alert">
          {note.text}
          <button type="button" className="btn ghost small" aria-label="Zamknij komunikat" onClick={() => setNote(null)}>
            ×
          </button>
        </p>
      )}
      {note && !note.error && (
        <Notice className="cal-note" resetKey={note} onClose={() => setNote(null)}>
          {note.text}
        </Notice>
      )}

      <div
        className={`cal-body${panel ? ' with-panel' : ''}${panel && wide ? ' panel-full' : ''}`}
        style={resizable ? ({ '--cal-share': `${calShare}%` } as CSSProperties) : undefined}
      >
        <div className={`cal-main${drafting ? ' picking-dates' : ''}`} aria-busy={list.isFetching} data-feature-id="events.shortcuts">
          {list.isLoading && <p className="muted" role="status">Wczytywanie…</p>}
          {shown === 'month' && (phone ? <PhoneMonthView anchor={anchor} events={events} today={today} renderList={renderList} onNewOnDay={interact.onNewOnDay} dayExtra={dayExtra} /> : <MonthView
                dayExtra={dayExtra}
                anchor={anchor}
                events={events}
                today={today}
                selectedId={selectedId}
                onOpen={open}
                {...interact}
                onPickDay={(day) => {
                  setAnchor(day);
                  pickView('week');
                }}
              />)}
          {shown === 'week' && (phone ? <PhoneWeekView anchor={anchor} events={events} today={today} renderList={renderList} dayExtra={dayExtra} /> : <WeekView anchor={anchor} events={events} today={today} selectedId={selectedId} onOpen={open} {...interact} dayExtra={dayExtra} />)}
          {shown === 'year' && (
            <YearView
              anchor={anchor}
              events={events}
              today={today}
              onOpen={open}
              {...interact}
              drag={undefined}
              onPickMonth={(day) => {
                setAnchor(day);
                pickView('month');
              }}
            />
          )}
          {shown === 'list' && list.data && <EventList events={events} names={names} selectedId={selectedId} onOpen={open} empty="Nie znaleziono wydarzeń." label="Lista wydarzeń" onNewOnDay={interact.onNewOnDay} />}
          {shown !== 'list' && <Legend extra={dayExtra && shown !== 'year' ? <TaskLegendKey dot={shown === 'month'} /> : null} />}
        </div>
        {resizable && (
          <PanelSplitter
            value={calShare}
            controls="event-panel"
            onChange={setSplit}
            onCommit={(v) => {
              setSplit(v);
              saveSplit(me.id, v);
            }}
          />
        )}
        {/* owner 2026-10-30 22:23: „Zapamiętać: „PFK” → …?” after a new event (the form is gone by then) */}
        <RememberRuleHost />
        {panel && (
          <SidePanel
            id="event-panel"
            label={panel.kind === 'new' ? (panel.what === 'rental' ? 'Nowe wypożyczenie' : 'Nowe wydarzenie') : 'Szczegóły wydarzenia'}
            focusKey={panel.kind === 'new' ? `new-${panel.what}` : panel.id}
            phone={phone}
            closeOnEscape={panel.kind === 'new'}
            onClose={closePanel}
          >
            {panel.kind === 'new' && panel.what === 'rental' && (
              <NewRentalForm calendar={calendarLink} initialDay={panel.day} initialDays={panel.days} onCreated={onCreated} onClose={closePanel} full={wide} onFullChange={setNewFull} phone={phone} />
            )}
            {panel.kind === 'new' && panel.what === 'event' && canPlan(me) && (
              <NewEventWizard calendar={calendarLink} initialDay={panel.day} initialDays={panel.days} onCreated={onCreated} onClose={closePanel} full={wide} onFullChange={setNewFull} phone={phone} />
            )}
            {panel.kind === 'new' && panel.what === 'event' && !canPlan(me) && (
              <EventForm
                onSaved={(saved) => {
                  madeByForm.current = true;
                  onCreated({ eventId: saved.id, notice: 'Wydarzenie utworzone.', tab: can(me, PACKLISTS_RESOURCE, 'view') ? 'packlist' : 'details', series: null, remind: true });
                }}
                onDone={() => {
                  if (madeByForm.current) madeByForm.current = false;
                  else closePanel();
                }}
                initialDay={panel.day}
                initialDays={panel.days}
                calendar={calendarLink}
              />
            )}
            {panel.kind === 'event' && <EventDetails
                key={panel.id}
                eventId={panel.id}
                onClose={closePanel}
                notice={landing?.id === panel.id ? landing.notice : createdNotice?.id === panel.id ? createdNotice.notice : undefined}
                initialTab={panel.tab ?? (linked === panel.id ? urlTab : 'details')}
                onTabChange={(t) => setCardUrl({ id: panel.id, tab: t, full: wide })}
                full={wide}
                onFullChange={(f) => setCardUrl({ id: panel.id, tab: tabFromParam(params.get('zakladka')), full: f }, true)}
                phone={phone}
              />}
          </SidePanel>
        )}
      </div>
      {taskId && <TaskDialog me={me} taskId={taskId} onClose={() => setTaskId(null)} />}
      {previewed && <EventPreview event={previewed} client={previewed.contractorId ? names.get(previewed.contractorId) : undefined} onClose={() => setPreview(null)} />}
      {moving && !moving.saving && (
        <div className="dialog-backdrop">
          <div className="dialog panel" role="alertdialog" aria-modal="true" aria-labelledby="cal-move-title">
            <DialogLayer onClose={cancelMove} />
            <h2 id="cal-move-title">
              Przenieść „{moving.name}” na {moving.dates}?
            </h2>
            <div className="actions">
              <button className="btn primary" type="button" autoFocus onClick={confirmMove}>
                Przenieś
              </button>
              <button className="btn" type="button" onClick={cancelMove}>
                Anuluj
              </button>
            </div>
          </div>
        </div>
      )}
      {deleting && (
        <div className="dialog-backdrop">
          <div className="dialog panel" role="alertdialog" aria-modal="true" aria-labelledby="cal-delete-title">
            <DialogLayer onClose={() => setDeleting(null)} />
            <h2 id="cal-delete-title">Usunąć wydarzenie?</h2>
            <p>„{deleting.name}” zniknie z kalendarza. {cmd('Z')} (cofnij) przywraca je razem ze sprzętówką.</p>
            <div className="actions">
              <button className="btn primary" type="button" autoFocus onClick={confirmDelete}>
                Usuń wydarzenie
              </button>
              <button className="btn" type="button" onClick={() => setDeleting(null)}>
                Anuluj
              </button>
            </div>
          </div>
        </div>
      )}
    </section>
  );
}

/** Space (P6): a quick look at the chosen event, by its bar; Escape or space closes it. It does not take focus. */
function EventPreview({ event: e, client, onClose }: { event: CalendarEvent; client?: string | undefined; onClose: () => void }) {
  const ref = useRef<HTMLDivElement>(null);
  const [pos, setPos] = useState<{ top: number; left: number } | null>(null);
  useLayoutEffect(() => {
    const anchor = document.querySelector(`.cal-main [data-event-id="${CSS.escape(e.id)}"]`) ?? document.querySelector('.cal-main');
    const r = anchor?.getBoundingClientRect();
    const width = ref.current?.offsetWidth ?? 320;
    if (!r) return;
    setPos({ top: Math.min(r.bottom + 6, window.innerHeight - 200), left: Math.max(8, Math.min(r.left, window.innerWidth - width - 8)) });
  }, [e.id]);
  const extra = describeEvent(e);
  return (
    <div ref={ref} className="cal-preview" role="dialog" aria-label={`Podgląd: ${eventName(e)}`} style={pos ? { top: pos.top, left: pos.left } : undefined}>
      <div className={`cal-preview-head${e.clientColor ? ' client-mark' : ''}`} data-status={e.status} style={e.clientColor ? markStyle(e.clientColor) : undefined}>
        <h3>{eventName(e)}</h3>
        <button type="button" className="btn ghost small" aria-label="Zamknij podgląd" onClick={onClose}>
          ×
        </button>
      </div>
      <dl className="details-list">
        {e.startsAt && <div><dt className="muted">{e.segments && e.segments.length > 1 ? 'Terminy' : 'Termin'}</dt><dd>{e.segments && e.segments.length > 1 ? formatSegments(e.segments) : formatEventRange(e.startsAt, e.endsAt)}</dd></div>}
        {e.location && <div><dt className="muted">Miejsce</dt><dd>{e.location}</dd></div>}
        {client && <div><dt className="muted">Klient</dt><dd>{client}</dd></div>}
        {extra && <div><dt className="muted">Status</dt><dd>{extra}</dd></div>}
        {e.crew && e.crew.length > 0 && <div><dt className="muted">Obsada</dt><dd>{e.crew.join(', ')}</dd></div>}
      </dl>
    </div>
  );
}

/** Tabs of the card in the address (Polish words, as the "Kosztorysy" list already links `zakladka=kosztorys`). */
const TAB_PARAMS: Record<EventTab, string> = { details: 'szczegoly', packlist: 'sprzetowka', inputlist: 'inputlista', crew: 'obsada', schedule: 'harmonogram', checklist: 'lista-kontrolna', quote: 'kosztorys', subrentals: 'dopozyczenia', profit: 'rentownosc', settlements: 'rozliczenia', payments: 'platnosci' };
function tabFromParam(value: string | null): EventTab {
  const found = (Object.keys(TAB_PARAMS) as EventTab[]).find((t) => TAB_PARAMS[t] === value);
  return found ?? 'details';
}

function Chevron({ dir }: { dir: 'left' | 'right' }) {
  return (
    <svg width="16" height="16" viewBox="0 0 16 16" aria-hidden="true">
      <path d={dir === 'left' ? 'M10 3 5 8l5 5' : 'M6 3l5 5-5 5'} fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}

/** What the bars' status stripes mean, under the calendar: each key is drawn exactly like a bar of a client without a colour. */
function Legend({ extra }: { extra?: ReactNode }) {
  return (
    <div className="cal-legend">
      {(Object.keys(EVENT_STATUSES) as EventStatus[]).map((s) => (
        <span key={s} className="cal-key">
          <span className="cal-bar cal-swatch" data-status={s} aria-hidden="true" />
          {EVENT_STATUSES[s]}
        </span>
      ))}
      <span className="cal-key">
        <span className="cal-bar cal-swatch needs-quote" aria-hidden="true" />
        Potrzebna wycena
      </span>
      {extra}
    </div>
  );
}
